import { signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { pipe, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { withToastr } from '@shared/store/toastr.feature';
import { tapResponse } from '@ngrx/operators';
import { getErrorMessage } from '@shared/helpers/error-message';
import { FEATURES } from '@shared/constants/routing-paths.constants';
import type { FeatureToggleKeys } from '@shared/interfaces/refreshed-component';
import { filterNil } from 'ngxtension/filter-nil';
import { parseBetaFeatures } from '@features/beta-features/helpers/parse-beta-features';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '@shared/tokens/global';
import { withTreeShakableDevTools } from '@shared/constants/store.features';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { immerPatchState } from 'ngrx-immer/signals';

interface BetaFeaturesStore {
  features: Record<FeatureToggleKeys, boolean> | null;
}

const initialState: BetaFeaturesStore = {
  features: null,
};

export const BetaFeaturesStore = signalStore(
  { providedIn: 'root' },
  withTreeShakableDevTools('betaFeatures'),
  withToastr('Beta features'),
  withState(initialState),
  withMethods((store) => ({
    setBetaFeatures(features: Record<FeatureToggleKeys, boolean>) {
      immerPatchState(store, { features });
    },
    changeBetaFeature(isActive: boolean, feature: FeatureToggleKeys) {
      immerPatchState(store, (state) => {
        if (state.features) {
          state.features = { ...state.features, [feature]: isActive };
        }
      });
    },
  })),
  withMethods(
    (
      store,
      httpClient = inject(HttpClient),
      appConfig = inject(APP_CONFIG),
    ) => ({
      getBetaFeatures$: rxMethod<void>(
        pipe(
          switchMap(() =>
            httpClient.get(`${appConfig.api.url}${FEATURES}`).pipe(
              tapResponse({
                next: (features) =>
                  store.setBetaFeatures(parseBetaFeatures(features)),
                error: (error: unknown) =>
                  store.showErrorToast(getErrorMessage(error).error),
              }),
            ),
          ),
        ),
      ),
      getBetaFeaturesFromLocalStorage$: rxMethod<void>(
        pipe(
          map(() => {
            const features = localStorage.getItem(FEATURES);
            return features ? JSON.parse(features) : '';
          }),
          filterNil(),
          tap((features: Record<FeatureToggleKeys, boolean>) =>
            store.setBetaFeatures(parseBetaFeatures(features)),
          ),
        ),
      ),
      changeBetaFeature$: rxMethod<{
        isActive: boolean;
        feature: FeatureToggleKeys;
      }>(
        pipe(
          tap(({ isActive, feature }) =>
            store.changeBetaFeature(isActive, feature),
          ),
          map(() => store.features()),
          filterNil(),
          tap((features) => {
            localStorage.setItem(FEATURES, JSON.stringify(features));
            store.setBetaFeatures(parseBetaFeatures(features));
          }),
        ),
      ),
    }),
  ),
);
